import isNil from 'lodash/isNil'
import { convertAmountToDefaultCurrency } from '@omnea-app/packages/universal/currency-utils/convertToDefaultCurrency'
import { CoreDataCurrency } from '@omnea-app/packages/universal/currency-utils/coreDataCurrency'
import { CurrencyCode } from '@omnea-app/packages/universal/currency-utils/getCurrencyCodes'
import { PaymentIntervalEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { assertNever } from '@omnea-app/packages/universal/type-guards/assertNever'
import { Dictionary, Nullish } from '@omnea-app/packages/universal/utility-types/utilityTypes'
import { Price } from '../../lambdas/getRequest/helpers/price'

type Input = {
  price: Price
  defaultCurrency?: CurrencyCode
  currenciesByCode?: Dictionary<CoreDataCurrency>
}

export const getAmountInDefaultCurrency = ({ price, defaultCurrency, currenciesByCode }: Input): number | undefined => {
  if (isNil(price.fxRate)) return

  if (price.currency === defaultCurrency) return price.amount

  if (!defaultCurrency) return Math.round((price.amount / price.fxRate) * 100) / 100

  const defaultCurrencyFxRate = currenciesByCode?.[defaultCurrency]?.fxRate ?? 1

  return (
    convertAmountToDefaultCurrency({
      value: price.amount,
      valueCurrencyCode: price.currency,
      defaultCurrencyCode: defaultCurrency,
      valueFxRate: price.fxRate,
      defaultFxRate: defaultCurrencyFxRate,
    }) ?? undefined
  )
}

const isContractTotalDerivable = (paymentInterval: Nullish<PaymentIntervalEnum>): boolean => {
  switch (paymentInterval) {
    case PaymentIntervalEnum.WEEK:
    case PaymentIntervalEnum.MONTH:
    case PaymentIntervalEnum.QUARTER:
    case PaymentIntervalEnum.EVERY_SIX_MONTHS:
    case PaymentIntervalEnum.YEAR:
      return false
    case PaymentIntervalEnum.ONE_OFF:
    case PaymentIntervalEnum.MULTI_YEAR:
    case PaymentIntervalEnum.EVERY_TWO_YEARS:
    case undefined:
    case null:
      return true
    default:
      // Legacy/non-canonical interval strings (e.g. the "ONE-OFF" hyphen variant) are treated as a flat total
      assertNever(paymentInterval)
      return true
  }
}

export const getTotalInDefaultCurrency = (input: Input): number | undefined => {
  if (!isContractTotalDerivable(input.price.paymentInterval)) return undefined
  return getAmountInDefaultCurrency(input)
}
